import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { ReactNode } from 'react';
import { Variants, motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { UIProps } from '../../theme/mixins';
import UI from '../UI';

export interface DragDropItem {
  id: string;
  title: string;
  children?: DragDropItem[];
}

interface DragDropListProps {
  items: DragDropItem[];
  onChange: (items: DragDropItem[]) => void;
}

export const DragDropList = ({ items, onChange }: DragDropListProps) => {
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.type === 'root') {
      // Dragging a root item
      const newItems = [...items];
      const [removed] = newItems.splice(result.source.index, 1);
      newItems.splice(result.destination.index, 0, removed);

      onChange(newItems);
    } else if (result.type === 'child') {
      // Dragging leaf item
      const sourceRootId = result.source.droppableId;
      const destinationRootId = result.destination.droppableId;
      const sourceRoot = items.filter((category) => category.id === sourceRootId)[0];

      const childIndex = sourceRoot.children.findIndex((child) => child.id === result.draggableId);
      const child = sourceRoot.children[childIndex];

      const newItems = items.map((item) => {
        const children = [...item.children];

        if (item.id === sourceRootId) {
          // Remove the leaf from the old root
          children.splice(childIndex, 1);
        }

        if (item.id === destinationRootId) {
          // Add the leaf to the new root
          children.splice(result.destination.index, 0, child);
        }

        return {
          ...item,
          children,
        };
      });

      onChange(newItems);
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd} nonce={window.NONCE_ID}>
      <Droppable droppableId="root" type="root" direction="vertical">
        {(provided) => (
          <UI.GridContainer sc={{ gutter: 0.25 }} ref={provided.innerRef} {...provided.droppableProps}>
            {items.map((item, index) => (
              <DraggableCard
                draggableId={item.id}
                index={index}
                key={item.id}
              >
                <UI.Div sc={{ strong: !!item.children }}>
                  {item.title}
                </UI.Div>
                {item.children && (
                  <Droppable droppableId={item.id} type="child" direction="vertical">
                    {(provided) => (
                      <UI.GridContainer
                        sc={{ gutter: 0.25, mt: item.children.length > 0 ? 2 : 0 }}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {item.children.map((child, index) => (
                          <DraggableCard
                            draggableId={child.id}
                            index={index}
                            key={child.id}
                          >
                            {child.title}
                          </DraggableCard>
                        ))}
                        {provided.placeholder}
                      </UI.GridContainer>
                    )}
                  </Droppable>
                )}
              </DraggableCard>
            ))}
            {provided.placeholder}
          </UI.GridContainer>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const motionAnimation: Variants = {
  initial: {
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05), 0 2px 6px rgba(0, 0, 0, 0.05)',
  },
  animate: {
    boxShadow: '0 20px 25px rgba(0,0,0,.1), 0 10px 10px rgba(0,0,0,.04)',
  },
};

const MotionCard = motion(
  styled(UI.Card)`
    ${({ theme }) => css`
      margin: 0;
      padding: 0;
      display: grid;
      grid-template-columns: fit-content(60px) 1fr;
      grid-template-rows: 1fr;
      align-items: center;
      overflow: hidden;

      > div {
        height: 100%;
      }

      .draggable-card {
        box-shadow: none !important;
        border: 1px solid ${theme.colors.gray[150]};
      }

      > div:first-child {
        background: ${theme.colors.gray[50]};

        > div {
          cursor: grab;
          padding: 13px ${theme.gutter / 2}px 11px ${theme.gutter / 2}px;
          opacity: 0.5;
          height: 100%;

          img {
            width: 16px;
          }
        }
      }

      > div:nth-child(2) {
        padding: ${theme.gutter / 2}px;
      }
    `}
  `,
);

interface DraggableCardProps {
  draggableId: string;
  index: number;
  dragAll?: boolean;
  sc?: UIProps;
  children?: ReactNode;
}

export const DraggableCard = ({ draggableId, index, dragAll = true, sc, children }: DraggableCardProps) => (
  <Draggable draggableId={draggableId} index={index}>
    {(provided, snapshot) => {
      const dragHandleProps = dragAll ? provided.dragHandleProps : {};

      return (
        <UI.Div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...dragHandleProps}
          sc={sc}
        >
          <MotionCard
            animate={snapshot.isDragging && !snapshot.isDropAnimating ? 'animate' : 'initial'}
            variants={motionAnimation}
            className="draggable-card"
          >
            <UI.Div {...provided.dragHandleProps}>
              <motion.div whileTap={{ opacity: 0.9 }}>
                <UI.Icon sc={{ color: 'gray.800' }}>
                  <UI.Icons.DragHandle />
                </UI.Icon>
              </motion.div>
            </UI.Div>
            <UI.Div>
              {children}
            </UI.Div>
          </MotionCard>
        </UI.Div>
      );
    }}
  </Draggable>
);
